import type { SavedCar } from '../core/save/model.js';
import { badgeElement, vehicleById } from '../content/vehicles.js';
import { deleteCarImage, getCarImage } from '../save/db.js';
import { bigButton, el, GLYPH, GLYPH_ICON, pictureButton } from './dom.js';
import { menuShell } from './menu-shell.js';
import type { Screen, ScreenContext } from './router.js';

/**
 * The Garage: every car she has ever painted.
 *
 * This is the emotional core of the whole game and the easiest thing to under-build. She will come
 * here just to look at her cars. It is also what makes the Draw phase *skippable* - by the fifth
 * session, re-colouring a truck before every mission would be a chore rather than the treat it is on
 * day one.
 *
 * Each card is her actual baked drawing, not a stock picture of a truck, with its pup's badge on the
 * corner: a shelf of her own colours needs something on it saying which pup each one is. Tap the car to
 * drive it, the crayon to change its colours, the bin to throw it away.
 *
 * Deleting takes two taps, and that is not optional. Her cars are the one genuinely unrecoverable thing
 * in this game - there is no way to apologise to a three-year-old for losing one - so the bin only ever
 * *asks*, and the second tap is a big obvious tick beside an equally big way out.
 */

/**
 * Six cells a page: three across and two down in landscape, two and three in portrait.
 *
 * Enough that the shelf still reads as a shelf, few enough that a card is a big picture of her car
 * rather than a stamp. The add-a-car card takes one of the six on the first page.
 */
const PER_PAGE = 6;

export interface GarageScreenOptions {
  /** Injected for tests, which have no IndexedDB. */
  readonly loadImage?: (carId: string) => Promise<Blob | undefined>;
  readonly deleteImage?: (carId: string) => Promise<void>;
}

export function createGarageScreen(options: GarageScreenOptions = {}): Screen {
  const loadImage = options.loadImage ?? getCarImage;
  const deleteImage = options.deleteImage ?? deleteCarImage;

  let disposed = false;
  const objectUrls: string[] = [];

  /** Every cell on the shelf, in order, so a deleted car can be taken out and the pages re-laid. */
  let cells: HTMLElement[] = [];
  let shell: ReturnType<typeof menuShell> | null = null;

  return {
    mount(root, ctx) {
      shell = menuShell({
        screenClass: 'screen--garage',
        badge: 'garage',
        badgeTitle: 'garage',
        onBack: () => ctx.dispatch({ type: 'BACK' }),
      });

      /*
       * The first cell is always "another one", and the shelf is newest first.
       *
       * It used to appear only when the garage was empty, which is exactly backwards: an empty garage is
       * the one time she has just come from the crayon, and a full one is when starting another car means
       * going home first to find the button. One card in one place, whatever is behind it.
       */
      cells = [addCard(ctx), ...[...ctx.save.garage].reverse().map((car) => card(car, ctx))];
      shell.paginate(cells, PER_PAGE);
      root.append(shell.screen);
    },

    unmount() {
      disposed = true;
      // Blob URLs are not garbage collected on their own; leaking one per car per visit would
      // eventually exhaust memory on a tablet.
      for (const url of objectUrls) URL.revokeObjectURL(url);
      objectUrls.length = 0;
      cells = [];
      shell = null;
    },
  };

  /**
   * Take a thrown-away car off the shelf.
   *
   * The pages are laid out again rather than the card simply being removed from the grid: with six to a
   * page, deleting one used to leave a hole and strand the car that should have moved up into it on the
   * page behind. Re-laying keeps her on the page she was looking at, which is the one with the gap in it.
   */
  function forget(cell: HTMLElement): void {
    cells = cells.filter((entry) => entry !== cell);
    shell?.paginate(cells, PER_PAGE);
  }

  function card(car: SavedCar, ctx: ScreenContext): HTMLElement {
    const thumb = el('div', { className: 'garage-thumb' });

    void loadImage(car.id)
      .then((blob) => {
        if (disposed || blob === undefined) return;
        const url = URL.createObjectURL(blob);
        objectUrls.push(url);
        const image = el('img', { className: 'garage-image', attrs: { alt: '' } });
        image.src = url;
        thumb.replaceChildren(image);
      })
      .catch(() => {
        // No thumbnail is a blank card, not a missing car - her colours are in the save either way.
      });

    // A card, but not a pressable one: what she aims at are the buttons on it, and a panel that dips
    // under her finger while she is aiming at something on it is a panel that makes her miss.
    const card = el('div', { className: 'garage-card card' });

    /*
     * Whose car this is, on the corner of the card - the same tag the picker and the studio wear.
     *
     * Her thumbnails are all painted by the same hands, so by the fifth session a shelf of them is a
     * shelf of *her colours* with nothing on it saying which pup each one belongs to. A car drawn on a
     * template this build does not know simply gets no tag.
     */
    const vehicle = vehicleById(car.templateId);
    if (vehicle !== null) {
      const badge = badgeElement(vehicle);
      badge.classList.add('pup-badge--card');
      card.append(badge);
    }

    /*
     * The picture *is* the drive button.
     *
     * It used to be a row of three equal buttons under the thumbnail, which put a 96px target beside a
     * 240px picture of her own car and made driving - by far the commonest thing she comes here to do -
     * the thing she had to aim at. Now the card is the car and the car is the button; the two rarer
     * actions are corner buttons over it, still bigger than the floor in `--tap-min`.
     */
    const drive = pictureButton([thumb], () => ctx.dispatch({ type: 'PICK_CAR', carId: car.id }), {
      className: 'garage-drive',
      title: 'drive this one',
    });

    const recolour = pictureButton([], () => ctx.dispatch({ type: 'RECOLOUR', carId: car.id }), {
      className: 'garage-corner garage-corner--crayon',
      title: 'colour it again',
      icon: GLYPH_ICON.crayon,
    });

    const bin = pictureButton([], () => card.classList.add('is-confirming'), {
      className: 'garage-corner garage-corner--bin',
      title: 'throw it away',
      icon: GLYPH_ICON.bin,
    });

    // The second tap. Shown over the card so the choice is unmistakably about *this* car, with the
    // way out first and larger - the safe option should be the easy one to hit.
    const confirm = el('div', {
      className: 'garage-confirm',
      children: [
        bigButton(GLYPH.back, () => card.classList.remove('is-confirming'), {
          variant: 'quiet',
          className: 'garage-button',
          icon: GLYPH_ICON.back,
        }),
        bigButton(
          GLYPH.bin,
          () => {
            ctx.updateSave((save) => ({
              ...save,
              garage: save.garage.filter((entry) => entry.id !== car.id),
            }));
            // The blob would otherwise sit in IndexedDB forever with nothing referencing it.
            void deleteImage(car.id).catch(() => undefined);
            forget(card);
          },
          { variant: 'go', className: 'garage-button garage-button--confirm', icon: GLYPH_ICON.bin },
        ),
      ],
    });

    card.append(drive, recolour, bin, confirm);
    return card;
  }
}

/**
 * Another one.
 *
 * A card rather than a corner button, because it takes the place of a car and that is what it makes.
 * An empty garage is this card on its own, which is the whole of what the empty state ever needed to
 * say: here is the thing that fills it.
 */
function addCard(ctx: ScreenContext): HTMLElement {
  return pictureButton([], () => ctx.dispatch({ type: 'NEW_CAR' }), {
    className: 'garage-add card card--pressable',
    title: 'colour another car',
    icon: GLYPH_ICON.crayon,
  });
}
